import * as Sentry from '@sentry/react';
import { readAuthSession } from './authSession';
import { SENTRY_EXTENSION_DENY_URLS, shouldDropNonActionableClientNoise } from './sentryNoise';

const SENSITIVE_KEY_PATTERN = /(authorization|cookie|password|token|secret|phone|email)/i;

function isSentryEnabled() {
  const enabled = (process.env.REACT_APP_SENTRY_ENABLED || '').trim().toLowerCase();
  if (enabled === 'false' || enabled === '0') return false;
  return Boolean((process.env.REACT_APP_SENTRY_DSN || '').trim());
}

function scrubObject(value, depth = 0) {
  if (!value || depth > 4) return value;

  if (Array.isArray(value)) {
    return value.map((item) => scrubObject(item, depth + 1));
  }

  if (typeof value !== 'object') {
    return value;
  }

  const next = { ...value };
  Object.keys(next).forEach((key) => {
    if (SENSITIVE_KEY_PATTERN.test(key)) {
      next[key] = '[Filtered]';
      return;
    }
    next[key] = scrubObject(next[key], depth + 1);
  });
  return next;
}

function parseNumber(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function getReleaseValue() {
  return process.env.REACT_APP_RELEASE || process.env.REACT_APP_GIT_SHA || undefined;
}

function shouldDropBreadcrumb(breadcrumb) {
  const category = breadcrumb?.category || '';
  if (SENSITIVE_KEY_PATTERN.test(category)) return true;

  const data = breadcrumb?.data;
  if (!data || typeof data !== 'object') return false;

  const keys = Object.keys(data);
  return keys.some((key) => SENSITIVE_KEY_PATTERN.test(key));
}

export { shouldDropNonActionableClientNoise };

export function setSentryUserFromAuthSession() {
  const { user, isAuthenticated } = readAuthSession();
  if (!isAuthenticated || !user?.id) {
    Sentry.setUser(null);
    return;
  }

  // Use stable, low-risk identifiers only.
  Sentry.setUser({
    id: String(user.id),
    role: user.role || 'unknown',
  });
}

export function setSentryRouteContext(pathname, search) {
  const route = `${pathname || ''}${search || ''}` || '/';
  Sentry.setTag('route', route);
}

export function captureSentryTestEvent() {
  Sentry.captureMessage('LLP redesign Sentry test event', 'warning');
}

export function captureClientError(error, context = {}) {
  if (!isSentryEnabled() || !error) return;
  Sentry.withScope((scope) => {
    if (context.source) {
      scope.setTag('error_source', context.source);
    }
    if (context.extra && typeof context.extra === 'object') {
      scope.setExtras(scrubObject(context.extra));
    }
    Sentry.captureException(error);
  });
}

function attachSentryQaHelpers() {
  if (typeof window === 'undefined' || process.env.NODE_ENV === 'production') return;
  window.LLP_SENTRY_TEST_TRIGGER = captureSentryTestEvent;
}

export function initSentry() {
  const enabled = isSentryEnabled();
  const release = getReleaseValue();
  if (!enabled) return;

  Sentry.init({
    enabled,
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV,
    release,
    sendDefaultPii: false,
    tracesSampleRate: parseNumber(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE, 0),
    denyUrls: SENTRY_EXTENSION_DENY_URLS,
    beforeSend(event) {
      if (shouldDropNonActionableClientNoise(event)) {
        return null;
      }

      const next = scrubObject(event);
      if (next?.user) {
        delete next.user.email;
        delete next.user.ip_address;
        delete next.user.username;
      }
      return next;
    },
    beforeBreadcrumb(breadcrumb) {
      if (shouldDropBreadcrumb(breadcrumb)) return null;
      return scrubObject(breadcrumb);
    },
    integrations: [Sentry.browserTracingIntegration()],
  });

  setSentryUserFromAuthSession();
  attachSentryQaHelpers();

  if (process.env.REACT_APP_SENTRY_TEST_EVENT_ON_BOOT === 'true') {
    captureSentryTestEvent();
  }
}
